import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
  HostListener,
} from '@angular/core';
import { DropdownItem } from '../dropdown/dropdown.component';
import { calculateDropdownPosition } from '../../utils/dropdown-position.util';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  standalone: false,
})
export class SelectComponent {
  /**
   * The label text for the field
   */
  label = input<string>('');

  /**
   * Whether the field is required
   */
  required = input<boolean>(false);

  /**
   * The placeholder text when no value is selected
   */
  placeholder = input<string>('Please choose an option');

  /**
   * The currently selected value (item id)
   */
  value = input<string>('');

  /**
   * Text to display for the required field message
   */
  requiredText = input<string>('*This field is required.');

  /**
   * Array of dropdown items to display
   */
  options = input<DropdownItem[]>([]);

  /**
   * Maximum height for the dropdown before scrolling
   */
  dropdownMaxHeight = input<string>('300px');

  /**
   * Whether the dropdown shows a search box that filters options by label
   */
  searchable = input<boolean>(false);

  /**
   * Placeholder text for the search box (when searchable)
   */
  searchPlaceholder = input<string>('Search...');

  /**
   * Whether the field is disabled (not interactive)
   */
  disabled = input<boolean>(false);

  /**
   * Whether the field has a validation error
   */
  hasError = input<boolean>(false);

  /**
   * The error message to display
   */
  errorMessage = input<string>('');

  /**
   * Emits when the selected value changes
   */
  valueChange = output<string>();

  /**
   * Emits when an option is selected from the dropdown
   */
  selectionChange = output<DropdownItem>();

  /**
   * Reference to the select field container
   */
  selectContainer = viewChild<ElementRef>('selectContainer');

  /**
   * Reference to the dropdown component
   */
  dropdownComponent = viewChild<any>('dropdown');

  /**
   * Whether the dropdown is currently open
   */
  isDropdownOpen = signal<boolean>(false);

  /**
   * The currently selected item
   */
  selectedItem = signal<DropdownItem | null>(null);

  /**
   * Whether dropdown should open above the input (for positioning)
   */
  dropdownOpenAbove = signal<boolean>(false);

  /**
   * Whether the field has been touched/blurred
   */
  private isTouched = signal<boolean>(false);

  /**
   * Display text (either selected label or placeholder)
   */
  displayText = computed(() => {
    const item = this.selectedItem();
    return item ? item.label : this.placeholder();
  });

  /**
   * Whether to show placeholder style
   */
  isPlaceholder = computed(() => !this.selectedItem());

  /**
   * Whether to show the error state
   * Shows error only when field has error, is touched, and dropdown is closed
   */
  showError = computed(() => {
    return this.hasError() && this.isTouched() && !this.isDropdownOpen();
  });

  constructor() {
    // Sync value input with selectedItem signal
    effect(() => {
      const val = this.value();
      const opts = this.options();

      if (val) {
        const item = opts.find(option => option.id === val);
        this.selectedItem.set(item || null);
      } else {
        this.selectedItem.set(null);
      }
    });
  }

  /**
   * Toggles the dropdown open/closed
   */
  toggleDropdown(): void {
    if (this.disabled()) {
      return;
    }
    if (this.isDropdownOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Opens the dropdown
   */
  openDropdown(): void {
    this.isTouched.set(false);
    this.checkDropdownPosition();
    this.isDropdownOpen.set(true);
  }

  /**
   * Closes the dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen.set(false);
    this.isTouched.set(true);
  }

  /**
   * Handles keyboard events on the trigger
   */
  onTriggerKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    const dropdown = this.dropdownComponent();

    // Open dropdown on Enter or Space or Arrow keys
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();

      if (!this.isDropdownOpen()) {
        this.openDropdown();
      } else if (dropdown) {
        // Pass keys to dropdown only once
        dropdown.onKeyDown(event);
      }
    }

    // Close on Escape
    if (event.key === 'Escape' && this.isDropdownOpen()) {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  /**
   * Handles item selection from dropdown
   */
  onItemSelected(item: DropdownItem): void {
    this.selectedItem.set(item);
    this.valueChange.emit(item.id);
    this.selectionChange.emit(item);
    this.closeDropdown();
  }

  /**
   * Checks if dropdown should open above or below based on available space
   */
  private checkDropdownPosition(): void {
    const dropdownHeight = parseInt(this.dropdownMaxHeight());
    const shouldOpenAbove = calculateDropdownPosition(
      this.selectContainer(),
      dropdownHeight
    );
    this.dropdownOpenAbove.set(shouldOpenAbove);
  }

  /**
   * Handles clicks outside the component to close dropdown
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const container = this.selectContainer();
    if (!container) return;

    const clickedInside = container.nativeElement.contains(
      event.target as Node
    );

    if (!clickedInside && this.isDropdownOpen()) {
      this.closeDropdown();
    }
  }
}
